import React from "react";
import ReactDOM from "react-dom/client";

import App from "./App";
import "./styles.css";

type TelegramUser = {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
};

type TgMainButton = {
  text: string;
  isVisible: boolean;
  isActive: boolean;
  setText: (text: string) => TgMainButton;
  show: () => TgMainButton;
  hide: () => TgMainButton;
  enable: () => TgMainButton;
  disable: () => TgMainButton;
  showProgress: (leaveActive?: boolean) => TgMainButton;
  hideProgress: () => TgMainButton;
  onClick: (cb: () => void) => TgMainButton;
  offClick: (cb: () => void) => TgMainButton;
  setParams: (p: {
    text?: string;
    color?: string;
    text_color?: string;
    is_active?: boolean;
    is_visible?: boolean;
  }) => TgMainButton;
};

type TgBackButton = {
  isVisible: boolean;
  show: () => TgBackButton;
  hide: () => TgBackButton;
  onClick: (cb: () => void) => TgBackButton;
  offClick: (cb: () => void) => TgBackButton;
};

type TgHapticFeedback = {
  impactOccurred: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
  notificationOccurred: (type: "error" | "success" | "warning") => void;
  selectionChanged: () => void;
};

type TgSafeAreaInset = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        ready: () => void;
        expand: () => void;
        close: () => void;
        initData: string;
        initDataUnsafe: { user?: TelegramUser };
        colorScheme?: "light" | "dark";
        themeParams?: Record<string, string>;
        MainButton: TgMainButton;
        BackButton: TgBackButton;
        HapticFeedback?: TgHapticFeedback;
        // Нативный confirm Telegram. На старых клиентах undefined — fallback на window.confirm.
        showConfirm?: (message: string, callback: (ok: boolean) => void) => void;
        // Появилось в Bot API 8.0. На старых клиентах undefined — поэтому всё опционально.
        safeAreaInset?: TgSafeAreaInset;
        contentSafeAreaInset?: TgSafeAreaInset;
        onEvent?: (event: string, cb: () => void) => void;
        offEvent?: (event: string, cb: () => void) => void;
      };
    };
  }
}

// Прокидываем safe-area из Telegram WebApp в CSS-переменные.
// safeAreaInset — отступы от системного UI устройства (notch, dynamic island).
// contentSafeAreaInset — отступы от UI самого Telegram (× Закрыть, индикатор подключения).
// Суммируем — нам нужен общий зазор от верхней/нижней границы viewport до безопасной зоны.
function setupSafeAreaVars() {
  const wa = window.Telegram?.WebApp;
  if (!wa) return;

  const update = () => {
    const sa = wa.safeAreaInset ?? { top: 0, bottom: 0, left: 0, right: 0 };
    const ca = wa.contentSafeAreaInset ?? { top: 0, bottom: 0, left: 0, right: 0 };
    const root = document.documentElement;
    root.style.setProperty("--tg-safe-top", `${sa.top + ca.top}px`);
    root.style.setProperty("--tg-safe-bottom", `${sa.bottom + ca.bottom}px`);
    root.style.setProperty("--tg-safe-left", `${sa.left + ca.left}px`);
    root.style.setProperty("--tg-safe-right", `${sa.right + ca.right}px`);
  };

  update();
  wa.onEvent?.("safeAreaChanged", update);
  wa.onEvent?.("contentSafeAreaChanged", update);
  // Намеренно НЕ подписываемся на viewportChanged: на iOS он триггерится при выезде
  // клавиатуры → safe-area-inset перерасчитывается → весь layout рывком уезжает и
  // возвращается. safeAreaChanged/contentSafeAreaChanged для нашей логики достаточно.
}

setupSafeAreaVars();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
